import { configureStore, isAction, type Middleware } from "@reduxjs/toolkit";
import {
  useDispatch,
  useSelector,
  type TypedUseSelectorHook,
} from "react-redux";

// TEMPORARILY DISABLED: Causes full page reload after login which breaks SPA behavior
// import { simpleUserClearingMiddleware } from "./middleware/simpleUserClearing";
import { authStateCleanupMiddleware } from "./middleware/authStateCleanup";
import adaptiveStudyReducer from "./slices/adaptiveStudySlice";
import authReducer from "./slices/authSlice";
import contactReducer from "./slices/contactSlice";
import dictionaryReducer from "./slices/dictionarySlice";
import learningReducer from "./slices/learningSlice";
import paymentsReducer from "./slices/paymentsSlice";
import practiceReducer from "./slices/practiceSlice";
import profileReducer from "./slices/profileSlice";
import registrationReducer from "./slices/registrationSlice";
import subscriptionReducer from "./slices/subscriptionSlice";
import dictationReducer, {
  restoreDraft,
} from "../features/dictation/store/dictationSlice";
import {
  clearDictationDraft,
  readDictationDraft,
  saveDictationDraft,
} from "../features/dictation/store/draftStorage";
import studentsReducer from "../features/students/store/studentsSlice";
import materialsReducer from "../features/texts/store/materialsSlice";
import textsReducer from "../features/texts/store/textsSlice";

const dictationDraftMiddleware: Middleware = (api) => (next) => (action) => {
  const result = next(action);
  if (!isAction(action)) return result;
  const state = api.getState() as {
    auth: { user: { id: string; targetLanguage?: string | null } | null };
    dictation: ReturnType<typeof dictationReducer>;
  };
  if (
    action.type === "auth/getCurrentUser/fulfilled" &&
    !state.dictation.session &&
    state.auth.user
  ) {
    const draft = readDictationDraft(state.auth.user.id);
    if (draft && draft.session.language === state.auth.user.targetLanguage)
      api.dispatch(restoreDraft(draft));
    else if (draft) clearDictationDraft();
  } else if (
    action.type === "auth/logout" ||
    action.type === "dictation/resetDictationState"
  ) {
    clearDictationDraft();
  } else if (action.type.startsWith("dictation/") && state.auth.user) {
    saveDictationDraft(state.auth.user.id, state.dictation);
  }
  return result;
};

export const store = configureStore({
  reducer: {
    auth: authReducer,
    contact: contactReducer,
    learning: learningReducer,
    registration: registrationReducer,
    practice: practiceReducer,
    profile: profileReducer,
    texts: textsReducer,
    materials: materialsReducer,
    students: studentsReducer,
    dictation: dictationReducer,
    payments: paymentsReducer,
    dictionary: dictionaryReducer,
    adaptiveStudy: adaptiveStudyReducer,
    subscription: subscriptionReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST"],
      },
    }).concat(dictationDraftMiddleware, authStateCleanupMiddleware),
  // TEMPORARILY DISABLED: simpleUserClearingMiddleware causes full page reload after login
  // .concat(simpleUserClearingMiddleware),
  devTools: import.meta.env.MODE !== "production",
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Typed hooks
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
